import React, { Component } from 'react';
import {
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  Chip,
  TextField,
  Select,
  MenuItem,
  AccordionSummary,
  Typography,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingButton from '../../utils/LoadingButton';
import { crmBase } from '../../../airtable_crm';
import logger from '../../../logger';
import { alertConfirm, alertError, alertSuccess, alertWarn } from '../../../alertDispatcher';
import { getCurrentMission, getCurrentSession, getCurrentUser } from '../../../dataModelHelpers';
import { dispatchMissionCreatedDeleted, MISSION_EVENTS } from '../../../missionEvents';
import { CustomCheckbox } from '../../utils/CustomCheckbox';
import { MissionLoader, MissionChecker, AWS } from '@rogoag/shared-ts';
import { Mission, LocalAtRecord } from '../../../db';
import { MissionState } from '../../../constants';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import EventBus from '../../../EventBus';
import { BoundaryChangeType } from '../../../types/types';
import { MyAccordion } from '../../utils';
import { MapMakingExitInterviewNotesVisible } from '../../../db/local_storage';
import { submissionCheckOutcome } from './submissionCheck';
import { createLocalAtRecordByAtModel } from '../../../db_ops/airtable_record_ops';
import { getLocalAtSearch } from '../../../db_ops/airtable_search_ops';

const SYNC_CANCELLED = 4;
const SYNC_SUCCESS = 3;
const SYNC_FAILED = 0;

interface MapsExitInterviewProps {
  closeForm: () => void;
  open: boolean;
}

const PriorityOVRD = [
  'Urgent',
  'Regular Turn (default)',
  'No Rush (turn+1wk)',
  'Flexible (before freezing)',
  'Winter Sample Acceptable',
  'Flexible (before growth stage)',
  'Flexible (before planting)',
  'In-crop Acceptable',
  'Flexible (within 2 weeks)',
] as const;

type PriorityOVRD = (typeof PriorityOVRD)[number];

const ADDITIONAL_JOB_FLAGS = [
  'Map Pivot (Ground obstacle)',
  'Map Powerlines (Air obstacle)',
  'Map Hard to See',
  'Map Misc',
];

interface MapsExitInterviewState {
  confirmNoButton: boolean;
  managerReviewed: boolean;
  pointsModified: boolean;
  mapIssue: boolean;
  newMissionState: MissionState;
  boundaryRecording: BoundaryChangeType;
  opNotes?: string;
  mapmakingNotesExpanded?: boolean;
  mapmakingRules?: string;
  priorityOVRDOriginal: PriorityOVRD;
  urgent: boolean;
  jobNotesReviewed: boolean;
  labInstructions?: string;
  submitFailed: boolean;
  checkSuccess: boolean;
  mapNotes: string | undefined;
  additionalJobFlags: string[];
}

export default class MapsExitInterview extends Component<MapsExitInterviewProps, MapsExitInterviewState> {
  closeTimeout: NodeJS.Timeout;

  constructor(props: MapsExitInterviewProps) {
    super(props);
    this.state = {
      confirmNoButton: false,
      managerReviewed: false,
      pointsModified: false,
      mapIssue: false,
      newMissionState: MissionState._4_Mission_Finalized,
      boundaryRecording: BoundaryChangeType.None,
      opNotes: '',
      mapmakingNotesExpanded: MapMakingExitInterviewNotesVisible.get(),
      mapmakingRules: '',
      priorityOVRDOriginal: 'Regular Turn (default)',
      urgent: false,
      jobNotesReviewed: false,
      labInstructions: '',
      submitFailed: false,
      checkSuccess: false,
      mapNotes: '',
      additionalJobFlags: [],
    };

    this.handleComplete = this.handleComplete.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.finalizeMission = this.finalizeMission.bind(this);
  }

  async componentDidMount() {
    await this.initializeFromAirtable();
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.initializeFromAirtable);
  }

  componentWillUnmount() {
    clearTimeout(this.closeTimeout);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.initializeFromAirtable);
  }

  handleCheck = async () => {
    const mission = getCurrentMission();

    if (!mission) {
      throw new Error('No mission found');
    }

    const kml = mission.to_kml({ insert_boxes: true });

    if (!kml) {
      throw new Error('No KML found');
    }

    let preventSubmission = false;
    let errors: any[] = [];
    let warnings: string[] = [];

    try {
      const sharedMissionLoader = new MissionLoader({ data: kml.toString(), filename: '.kml' }); //, { expectedType: mission.getJob()?.sites_type, sampleIdColumn: "SampleID" });
      const sharedMission = await sharedMissionLoader.parse();
      const sharedMissionChecker = new MissionChecker(sharedMission);
      errors = sharedMissionChecker.validateRunnable();

      const validationResult = mission.validateBeforeMapMakingSubmission();
      preventSubmission = validationResult.preventSubmission;
      errors = errors.concat(validationResult.errors);

      // The robot-path advisory drives the mission on pathfinder's model. Its
      // errors are advisory like the geometry ones (they ask for confirmation),
      // and a pathfinder outage is a warning, never a block.
      const robotPathResult = await mission.validateRobotPath();
      errors = errors.concat(robotPathResult.errors);
      warnings = warnings.concat(robotPathResult.warnings);

      this.setState({ checkSuccess: errors.length === 0 });
    } catch (err) {
      errors.push(err);
    }

    // Tell the mapmaker about checks that could not run before the outcome is
    // decided — they neither block nor ask for confirmation.
    for (const warning of warnings) {
      alertWarn(warning);
    }

    const outcome = submissionCheckOutcome(preventSubmission, errors);

    if (outcome !== 'ok') {
      for (const error of errors) {
        if (typeof error === 'object' && 'rogo' in error && 'message' in error) {
          alertWarn(`Found mission problem: ${error.message}`);
        } else {
          alertWarn(`Found issue in mission: ${JSON.stringify(error)}`);
        }
      }
    }

    if (outcome === 'block') {
      return false;
    }

    if (outcome === 'confirm') {
      const confirm = await alertConfirm('There are issues with this mission, are you sure you want to upload?');

      if (!confirm) {
        return false;
      }
    }

    return true;
  };

  async handleComplete() {
    this.setState({ submitFailed: false });
    const mission = getCurrentMission();

    if (!mission) {
      alertError('No mission found, try to reload (in the sidebar).');

      this.setState({ submitFailed: true });

      return;
    }

    const jobId = mission.job_id;

    let startTime = performance.now();
    const result = await this.finalizeMission(mission);
    let duration = performance.now() - startTime;
    await logger.log(`MapExitInterview::handleComplete - took ${duration.toFixed(0)} ms to finalize mission`, result);

    if (result === SYNC_SUCCESS) {
      if (jobId) {
        try {
          AWS.Lambda.PreSamplingProcessor.runPostMapmakingTasks(jobId);
        } catch (e) {
          await logger.log('POST_MAPMAKING_TASKS_FAILED', e);
        }
      }

      // TODO if something failed, we should revert somehow...

      startTime = performance.now();
      await this.handleClear({ resetMapmaker: false });
      duration = performance.now() - startTime;
      await logger.log(`MapExitInterview::handleComplete - took ${duration.toFixed(0)} ms to handleClear`, result);

      alertSuccess(`Successfully updated job in Airtable ${jobId || ''}`);
    } else if (result === SYNC_FAILED) {
      this.setState({ submitFailed: true });
    }
  }

  async handleClose() {
    await logger.log('HANDLE_CLOSE');

    if (!this.state.confirmNoButton) {
      this.setState({ confirmNoButton: true });
      this.closeTimeout = setTimeout(() => {
        this.setState({ confirmNoButton: false });
      }, 2000);
    } else {
      await this.handleClear({ resetMapmaker: true });
    }
  }

  async handleClear({ resetMapmaker = false }) {
    const start = performance.now();
    await logger.log('HANDLE_CLEAR - start');

    const session = getCurrentSession();

    if (!session) {
      alertError('No session found, try to reload (in the sidebar).');

      return;
    }

    const currentMission = getCurrentMission();

    if (!currentMission) {
      alertError('No mission found, try to reload (in the sidebar).');

      return;
    }

    const job = await currentMission.get_airtable_job_record();

    // we will only enter this if this mapmaker did NOT create the mission
    const user = getCurrentUser();
    const mapmakers = job?.get('Mapmaker') as string[];
    const multipleMapmakers = mapmakers?.length > 1;
    const hasIntMission = job
      ? job.getAttachments().filter((attachment) => attachment?.field_name === 'Int Mission').length > 0
      : false;

    const duration = performance.now() - start;
    await logger.log(`HANDLE_CLEAR - took ${duration.toFixed(0)} ms from the start`, {
      jobId: job?.id,
      user: user?.name,
      resetMapmaker,
      hasIntMission,
      multipleMapmakers,
    });

    // A user can skip upload or upload a mission, which is what determines the resetMapmaker flag
    // However, if a user re-opens a map that THEY made, we don't want to reset the mapmaker
    // if they skip upload and don't change anything. In this case, they would be the only mapmaker
    // set because we filtered for unique mapmakers when we opened the map. So the "multipleMapmakers"
    // flag can tell us that.
    if (job && mapmakers && resetMapmaker && (!hasIntMission || multipleMapmakers)) {
      // remove the current user from the mapmakers
      if (user && user.id) {
        const index = mapmakers.indexOf(user.id);

        if (index > -1) {
          mapmakers.splice(index, 1);
        }
      }

      await job.set('Mapmaker', mapmakers);
    }

    if (job && this.state.mapIssue) {
      await job.set('Job Flag Notes', this.state.mapNotes);
      await job.set('Job Flags', [
        ...new Set(this.state.additionalJobFlags.concat(this.getJobOtherFlags(job), ['Map Issue'])),
      ]);
    }

    if (job && job.dirty) {
      try {
        await job.sync();
      } catch (e) {
        const closeWithoutSync = await alertConfirm(
          'Failed to sync mission with Airtable. Do you wish to exit without saving it?',
        );

        if (!closeWithoutSync) {
          return;
        }
      }
    }

    session.Mission_id = undefined;
    dispatchMissionCreatedDeleted();

    // update the UI
    this.props.closeForm();
  }

  private getJobOtherFlags(job: LocalAtRecord<JobsFieldSet>) {
    return (job.get('Job Flags') || []).filter((f) => !ADDITIONAL_JOB_FLAGS.includes(f));
  }

  async finalizeMission(mission: Mission) {
    const missionName = mission.getMissionName('INT');

    try {
      let record = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', mission.job_id!);

      if (!record) {
        await getLocalAtSearch<JobsFieldSet>('Jobs', '', `{Rogo Job ID} = '${mission.job_id}'`, true);
        record = LocalAtRecord.getRecord('Jobs', mission.job_id!);
      }

      if (!record) {
        alertWarn('AT record not found');

        return SYNC_FAILED;
      }

      let startTime = performance.now();
      const kml = mission.to_kml({ insert_boxes: true });

      if (!kml) {
        throw new Error('No KML found');
      }

      let duration = performance.now() - startTime;
      await logger.log(`MapExitInterview::finalizeMission - took ${duration.toFixed(0)} ms for mission.to_kml`);

      startTime = performance.now();
      const readyToUpload = await this.handleCheck();

      if (!readyToUpload) {
        return SYNC_CANCELLED;
      }

      duration = performance.now() - startTime;
      await logger.log(`MapExitInterview::finalizeMission - took ${duration.toFixed(0)} ms to handleCheck`);

      await record.clear_attachments('Int Mission');
      const attachment = await record.new_attachment('Int Mission');
      const kmz = await kml.toKMZ(`${missionName}.kml`);
      // @ts-ignore
      await attachment.write(`${missionName}`, 'kmz', new Blob([kmz], { type: 'application/vnd.google-earth.kmz' }));

      const boundaryFileExists = record.get('Bnd Shp');

      if (!boundaryFileExists) {
        // If the boundary file doesn't exist, set it too.
        await record.clear_attachments('Bnd Shp');

        const boundaryShapeFile = await mission.to_shapefile(false, true);

        if (boundaryShapeFile) {
          const bndAttachment = await record.new_attachment('Bnd Shp');
          await bndAttachment.write(`${mission.name}_bnds.zip`, 'zip', boundaryShapeFile);
        } else {
          alertWarn('Failed to set Bnd Shp on a mission - mission conversion to shapefile failed.');
        }
      }

      await record.set('Mission Status', this.state.newMissionState);

      if (this.state.managerReviewed) {
        await record.set('Ops Mgmt Reviewed?', 'Reviewed');
      }

      const otherJobFlags = this.getJobOtherFlags(record);
      const relevantJobFlags = this.state.mapIssue
        ? [...new Set(this.state.additionalJobFlags.concat(otherJobFlags, ['Map Issue']))]
        : [...new Set(this.state.additionalJobFlags.concat(otherJobFlags).filter((flag) => flag !== 'Map Issue'))];

      await record.set('Job Flags', relevantJobFlags);

      await record.set('Int Miss Edit?', this.state.pointsModified);

      // TODO why do we clear these on maps submission? Perhaps we should leave them alone?
      await record.set('Error Notes (auto)', '');

      if (this.state.opNotes !== undefined && this.state.opNotes !== '') {
        await record.set('Warnings/Instructions for Field (custom) #ops', this.state.opNotes);
      }

      await record.set('Lab Instructions', this.state.labInstructions || '');

      // With AirTable single select, the 'empty' option is actually null, not the empty string
      await record.set('Boundary Change Type', this.state.boundaryRecording || null);

      await record.set('Job Notes Reviewed', this.state.jobNotesReviewed);

      // Logic for setting Priority OVRD:
      // - If it was originally urgent, set it to an empty value
      // - If it was originally something else, set it back to that
      // - If the urgent flag is checked, set the Priority OVRD to urgent
      await record.set(
        'Priority OVRD',
        this.state.urgent
          ? 'Urgent'
          : this.state.priorityOVRDOriginal === 'Urgent'
            ? null
            : this.state.priorityOVRDOriginal,
      );

      // set the map maker
      const user = getCurrentUser();

      if (user && user.id) {
        console.log('ADDING', user);
        await record.set('Mapmaker', [user.id]);
      }

      await record.sync();

      return SYNC_SUCCESS;
    } catch (e) {
      console.error('MapsExitInterview - finalizeMission: error', e);

      return SYNC_FAILED;
    }
  }

  async autoFillLabInstructions(job: LocalAtRecord<JobsFieldSet>, mission: Mission) {
    try {
      let labInstructions = job.get('Lab Instructions') ?? '';

      if (!mission.getJob()?.auto_fill_lab_instructions) {
        console.log('Auto fill lab instructions is off');

        return labInstructions;
      }

      const deal = LocalAtRecord.getRecord('Deals', job.get('Deal')?.[0] || '');
      const type = deal?.get('Sites Type #serviceinfo');
      const customizer = deal?.get('Config/Deal Type Customizer');
      const recType = customizer === 'Composite' ? 'Composite' : type === 'ModGrid' ? 'Grid' : type;

      const recTypeString = recType ? `Rec Type: ${recType}` : '';

      if (recType && labInstructions.includes(recTypeString) === false) {
        console.log('Adding rec type to lab instructions');
        labInstructions = recTypeString + '\n' + labInstructions;
      }

      const salespersonIds = job.get('Salesperson Contacts') as [] | undefined;

      if (salespersonIds && salespersonIds.length > 0) {
        // Before refactor: Airtable.getRecordsByIds (which would call .refresh())
        const salespeople = await crmBase.contacts.get(salespersonIds);
        salespeople.forEach(createLocalAtRecordByAtModel);

        const agronomist = salespeople.map((s) => `${s.name} - ${s.mobilePhone}`).join('; ');

        if (agronomist && labInstructions.includes(agronomist) === false) {
          console.log('Adding agronomist to lab instructions');
          labInstructions = `Agronomist: ${agronomist}` + '\n' + labInstructions;
        }
      }

      const lastCrop = job.get('Last Crop') as string | undefined;
      const nextCrop = job.get('Next Crop') as string | undefined;
      const yieldGoal = job.get('Yield Goal & Unit') as string | undefined;

      const lastCropText = lastCrop ? `Last Crop: ${lastCrop}` : '';
      const nextCropText = nextCrop ? `Next Crop: ${nextCrop}` : '';
      const yieldGoalText = yieldGoal ? `Yield Goal: ${yieldGoal}` : '';
      const recInfo = [lastCropText, nextCropText, yieldGoalText].filter((text) => text !== '').join(' - ');

      if (recInfo && labInstructions.includes(recInfo) === false) {
        console.log('Adding rec info to lab instructions');
        labInstructions = `Rec Info: ${recInfo}` + '\n' + labInstructions;
      }

      return labInstructions;
    } catch (e) {
      console.error('Failed to auto-fill lab instructions', e);

      return job.get('Lab Instructions') ?? '';
    }
  }

  async initializeFromAirtable() {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    const job = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', mission.job_id!);

    if (job) {
      const labInstructions = await this.autoFillLabInstructions(job, mission);
      console.log('Auto-filled lab instructions:', labInstructions);

      // “Rec Info”: Prev Crop, Next Crop, Yield Goal
      // Agronomist -> Agronomist Name & cell #
      //    Rec Type: (zone, grid, composite)
      //    Grids are grids
      //    Modgrids are grids (likely not used)
      //    Zones are either
      //        Zones (default)
      //        Composite (if deal/config customizer contains composite)
      //            Example of this: 1-4 sample composite
      this.setState({
        managerReviewed: job.get('Ops Mgmt Reviewed?') === 'Reviewed',
        pointsModified: job.get('Int Miss Edit?') as boolean,
        opNotes: job.get('Warnings/Instructions for Field (custom) #ops') as string | undefined,
        priorityOVRDOriginal: (job.get('Priority OVRD') as PriorityOVRD) || 'Regular Turn (default)',
        urgent: (job.get('Priority OVRD') as PriorityOVRD) === 'Urgent',
        jobNotesReviewed: !!job.get('Job Notes Reviewed'),
        labInstructions: labInstructions,
        mapmakingRules: mission.getJob()?.map_making_rules || '',
        mapIssue: job.get('Job Flags')?.includes('Map Issue') || false,
        mapNotes: job.get('Job Flag Notes') as string | undefined,
        boundaryRecording: job.get('Boundary Change Type') as BoundaryChangeType,
        additionalJobFlags: ((job.get('Job Flags') as string[]) || []).filter((flag) =>
          ADDITIONAL_JOB_FLAGS.includes(flag),
        ),
      });
    }
  }

  sanitizeMapmakingNotes = (mapMakingNotes: string | undefined) => {
    if (!mapMakingNotes) {
      return '';
    }

    // remove all undefined strings
    const sanitizedNotes = mapMakingNotes.replace(/undefined/g, '');

    // remove all <b>, </b>, and all <a> tags and content
    return sanitizedNotes.replace(/<b>|<\/b>|<a.*?>.*?<\/a>/g, '');
  };

  render() {
    return (
      <Dialog
        disableEscapeKeyDown
        maxWidth={'lg'}
        open={this.props.open || false}
        fullWidth
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }

          // doesn't actually do anything, not used elsewhere
          // this.setState({
          //   open: false
          // });
        }}
      >
        <DialogContent>
          <Grid container spacing={1} size={12}>
            <Grid style={{ marginRight: '12px' }}>
              <Select
                value={this.state.newMissionState}
                onChange={(e) => {
                  this.setState({ newMissionState: e.target.value as MissionState });
                }}
              >
                {Object.keys(MissionState).map((item, idx) => (
                  <MenuItem key={idx} value={MissionState[item]}>
                    {MissionState[item]}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <CustomCheckbox
                    checked={this.state.managerReviewed}
                    color="primary"
                    onChange={(e) => {
                      this.setState({ managerReviewed: e.target.checked });
                    }}
                  />
                }
                label="Manager reviewed?"
              />
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <CustomCheckbox
                    checked={this.state.pointsModified}
                    color="primary"
                    onChange={(e) => {
                      this.setState({ pointsModified: e.target.checked });
                    }}
                  />
                }
                label="Points modified?"
              />
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <CustomCheckbox
                    checked={this.state.mapIssue}
                    color="primary"
                    onChange={(e) => {
                      this.setState({ mapIssue: e.target.checked });
                    }}
                  />
                }
                label="Map Issue?"
              />
            </Grid>

            <Grid>
              <Button
                variant="outlined"
                onClick={() => {
                  let opNotes = this.state.opNotes;

                  if (!this.state.opNotes) {
                    opNotes = '';
                  } else {
                    opNotes += '\n';
                  }

                  opNotes += this.sanitizeMapmakingNotes(getCurrentMission()?.getJob()?.map_making_rules);
                  this.setState({ opNotes });
                }}
              >
                Insert Mapmaking Notes
              </Button>
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={
                  <CustomCheckbox
                    checked={this.state.urgent}
                    color="primary"
                    onChange={(e) => {
                      this.setState({ urgent: e.target.checked });
                    }}
                  />
                }
                label="Urgent"
              />
              <FormControlLabel
                control={
                  <CustomCheckbox
                    checked={this.state.jobNotesReviewed}
                    color="primary"
                    onChange={(e) => {
                      this.setState({ jobNotesReviewed: e.target.checked });
                    }}
                  />
                }
                label="Job Notes Reviewed?"
              />
              <FormControlLabel
                control={
                  <CustomCheckbox
                    checked={this.state.boundaryRecording === BoundaryChangeType.Major}
                    color="primary"
                    onChange={(e) => {
                      this.setState({
                        boundaryRecording: e.target.checked ? BoundaryChangeType.Major : BoundaryChangeType.None,
                      });
                    }}
                  />
                }
                label="Boundary Recording Required?"
              />
              <FormControlLabel
                labelPlacement="start"
                control={
                  <Select
                    multiple
                    value={this.state.additionalJobFlags}
                    onChange={(e) => {
                      this.setState({ additionalJobFlags: e.target.value as string[] });
                    }}
                    renderValue={(selected) => (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} style={{ margin: 2 }} />
                        ))}
                      </div>
                    )}
                  >
                    {ADDITIONAL_JOB_FLAGS.map((jobFlag) => (
                      <MenuItem key={jobFlag} value={jobFlag}>
                        {jobFlag}
                      </MenuItem>
                    ))}
                  </Select>
                }
                label="Job Flags"
              />
            </Grid>

            <Grid style={{ marginTop: '12px', marginBottom: '12px' }} size={12}>
              <TextField
                label="Field instructions/warnings for operator"
                multiline
                minRows={3}
                fullWidth
                variant="outlined"
                value={this.state.opNotes}
                onChange={(e) => {
                  this.setState({ opNotes: e.target.value });
                }}
              />
            </Grid>
            {/* Add a conditionally collapsed field that shows the full mapping instructions */}
            <Grid style={{ marginTop: '12px', marginBottom: '12px' }} size={12}>
              <TextField
                label="Lab Special Instructions"
                multiline
                minRows={3}
                fullWidth
                variant="outlined"
                value={this.state.labInstructions}
                onChange={(e) => {
                  this.setState({ labInstructions: e.target.value });
                }}
              />
            </Grid>
            <Grid style={{ marginTop: '12px', marginBottom: '12px' }} size={12}>
              <MyAccordion
                defaultExpanded={this.state.mapmakingNotesExpanded}
                elevation={3}
                onChange={(_event, expanded) => {
                  console.log('EXPANDED', expanded);
                  this.setState({ mapmakingNotesExpanded: expanded });
                  MapMakingExitInterviewNotesVisible.set(expanded);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Mapmaking Notes</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div style={{ color: 'black' }}>
                    {this.state.mapmakingRules?.length ? (
                      this.state.mapmakingRules.split('\n').map((item, key) => {
                        return <span key={key} dangerouslySetInnerHTML={{ __html: item + '<br />' }}></span>;
                      })
                    ) : (
                      <div>No notes found.</div>
                    )}
                  </div>
                </AccordionDetails>
              </MyAccordion>
            </Grid>
            {this.state.mapIssue && (
              <Grid style={{ marginBottom: '12px' }} size={12}>
                <TextField
                  label="Backoffice Notes / Map Issues (Required)"
                  multiline
                  minRows={3}
                  fullWidth
                  variant="outlined"
                  value={this.state.mapNotes}
                  onChange={(e) => {
                    this.setState({ mapNotes: e.target.value });
                  }}
                />
              </Grid>
            )}
          </Grid>
          {this.state.submitFailed && (
            <div style={{ color: 'red' }}>
              <b>Something went wrong submitting to Airtable. Try again or email mission file to ops support.</b>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <React.Fragment>
            <LoadingButton
              variant="outlined"
              color="secondary"
              data-testid="exit-interview-yes"
              disabled={this.state.mapIssue && !this.state.mapNotes}
              onClick={this.handleComplete}
            >
              Check & Upload
            </LoadingButton>

            <LoadingButton
              variant="outlined"
              color="secondary"
              data-testid="exit-interview-no"
              disabled={this.state.mapIssue && !this.state.mapNotes}
              onClick={this.handleClose}
            >
              {this.state.confirmNoButton ? 'Are you sure?' : this.state.mapIssue ? 'Skip + Flag' : 'Skip Upload'}
            </LoadingButton>
          </React.Fragment>
          <Button variant="outlined" onClick={this.props.closeForm}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
